import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useContacts } from '../../hooks/useContacts';
import { contactsService } from '../../services/contactsService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { Pencil, Trash, CaretLeft, User } from 'phosphor-react';

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { contacts, loading, error, refreshContacts } = useContacts();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [deleting, setDeleting] = useState(false);

  // Find the contact from the contacts list
  const contact = contacts.find(c => c.id === id);

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      setDeleting(true);
      const { error } = await contactsService.deleteContact(id);
      if (error) {
        alert(error.message);
      } else {
        refreshContacts();
        navigate('/contacts');
      }
    } catch (err) {
      alert('Failed to delete contact');
    } finally {
      setDeleting(false);
    }
  };

  // Show loading while authentication is in progress or business is not loaded
  if (authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {authLoading ? 'Loading authentication...' : 'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can view contacts.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the contact management system.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contact Not Found</h2>
          <p className="text-gray-600 mb-4">
            The contact you're looking for doesn't exist or has been deleted.
          </p>
          <Link to="/contacts">
            <Button variant="primary">Back to Contacts</Button>
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Contacts', href: '/contacts' },
    { label: contact.name, current: true }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Contact Details</h1>
        <div className="flex space-x-3">
          <Link to={`/contacts/${contact.id}/edit`}>
            <Button variant="primary" className="bg-blue-600 hover:bg-blue-700 flex items-center">
              <Pencil className="w-4 h-4 mr-2" weight="bold" />
              Edit Contact
            </Button>
          </Link>
          {contact.is_editable && (
            <Button
              variant="primary"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center"
            >
              <Trash className="w-4 h-4 mr-2" weight="bold" />
              {deleting ? 'Deleting...' : 'Delete Contact'}
            </Button>
          )}
        </div>
      </div>

      {/* Contact Details Card */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-8">
          <div className="flex items-center space-x-6 mb-8">
            {/* Profile Picture */}
            <div className="flex-shrink-0">
              {contact.profile_picture_url ? (
                <img
                  src={contact.profile_picture_url}
                  alt={contact.name}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-700" weight="bold" />
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{contact.name}</h2>
              <div className="flex items-center space-x-4">
                <span className="badge badge-info text-lg px-3 py-1">
                  {contactsService.getContactTypeLabel(contact.type)}
                </span>
                <span className="text-lg text-gray-600">
                  ID: {contact.contact_id}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">
                    {contact.email || <span className="text-gray-400">Not provided</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="text-sm text-gray-900">
                    {contact.phone || <span className="text-gray-400">Not provided</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="text-sm text-gray-900">
                    {contact.address ? (
                      <div className="whitespace-pre-line">{contact.address}</div>
                    ) : (
                      <span className="text-gray-400">Not provided</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Financial Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Current Balance</dt>
                  <dd className={`text-lg font-semibold ${
                    contact.balance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {contactsService.getFormattedBalance(contact.balance)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Metadata */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
              <div>
                <span className="font-medium">Created:</span>{' '}
                {new Date(contact.created_at).toLocaleDateString()}
              </div>
              <div>
                <span className="font-medium">Last Updated:</span>{' '}
                {new Date(contact.updated_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;